// The session transport's wire shapes (server plan §4.4; server/src/platform/identity/routes.ts): what sign-in,
// refresh and switch answer. Declared here so the backoffice and the server agree; the routes themselves are not
// operations (they set the httpOnly refresh cookie), so they are listed on the A14 allow-list, not in the catalog.
import { z } from 'zod';

export const AUTH_CLIENT_HEADER = 'x-radish-client';

export const LocationChoice = z.object({
  locationId: z.uuid(),
  name: z.string(),
  slug: z.string(),
  timezone: z.string(),
  currency: z.string(),
  roleName: z.string(),
  /** Location Info's email (staff Accounts list, legacy AccountCard). */
  email: z.string().nullable().optional(),
  /** When a drawer last opened there (legacy `last_drawer_open_at`, the Accounts list activity chip). */
  lastDrawerOpenedAt: z.iso.datetime({ offset: true }).nullable().optional(),
});

export const AccountChoice = z.object({
  accountId: z.uuid(),
  name: z.string(),
  via: z.enum(['membership', 'reseller', 'radish_staff']),
  /** The staff Accounts list's card (legacy AccountCard): the reseller's name and the account's contact. */
  resellerName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  locations: z.array(LocationChoice),
});

export const SessionView = z.object({
  user: z.object({ userId: z.uuid(), name: z.string(), email: z.string(), staffRole: z.enum(['admin', 'support', 'viewer']).nullable() }),
  current: z.object({
    accountId: z.uuid(),
    locationId: z.uuid().nullable(),
    via: z.enum(['membership', 'reseller', 'radish_staff']),
    roleName: z.string(),
    permissions: z.array(z.string()),
  }).nullable(),
  accounts: z.array(AccountChoice),
});

/** What sign-in, refresh, switch and passkey sign-in answer (the refresh token travels only in the cookie). */
export const SignedIn = z.object({
  session: SessionView,
  /** Null when the person must pick an account first (Radish staff, resellers). */
  accessToken: z.string().nullable(),
  /** Epoch milliseconds. */
  expiresAt: z.number().nullable(),
});

/**
 * The body of `POST /v1/auth/refresh` from a tab that acts somewhere (ops plan 2026-10-23-tab-scoped-refresh, WF-006,
 * REG-003): the account and location this tab acts at. Every tab of a browser shares one refresh cookie, so the refresh
 * mints its token at this scope, re-checked as a switch is, and changes nothing stored: not the session's context, the
 * next sign-in's default location or any staff switch. A scope the person may no longer act at answers 403
 * `scope_lost`, and the tab goes to the location picker (A192). No body: the session's own context, as before.
 */
export const RefreshScope = z.object({ accountId: z.uuid(), locationId: z.uuid().nullable() });
/** The refusal of a scoped refresh whose account or location this person may no longer act at. */
export const REFRESH_SCOPE_LOST = 'scope_lost';

/**
 * Invite email verification (A191, ops plan 2026-10-24-invite-email-verification). A `link` invite's link was shown to
 * the inviter, so holding it proves nothing about the address: the invitee asks `POST /v1/auth/invites/code` for a code
 * mailed to the invite's own address, and Accept carries it. An `email` invite asks for none (`verify` null). A server
 * before A191 sends no `verify`; a client before it sends no code and meets `code_required`, so it fails closed.
 */
export const INVITE_CODE_LENGTH = 6;
/** How long a sent code is good for. */
export const INVITE_CODE_TTL_MS = 10 * 60_000;
/** Wrong codes that void the one sent (a new one may be asked for, on the invitation mail allowance). */
export const INVITE_CODE_MAX_WRONG = 5;
/**
 * Wrong codes for one invited address in its account, over every code and every link sent to it, before its link ends and
 * no link to it takes a code for seven days (review of #2115): neither a Resend nor a reissued link renews them.
 */
export const INVITE_LINK_MAX_WRONG = 15;
/** How soon the accept page offers Resend. */
export const INVITE_CODE_RESEND_AFTER_MS = 60_000;
export const InviteCode = z.string().regex(/^[0-9]{6}$/);
/** Accept's refusals for a link invite's code: none sent with it, wrong (or voided, or used), or older than ten minutes. */
export const INVITE_CODE_REFUSALS = ['code_required', 'code_invalid', 'code_expired'] as const;
export type InviteCodeRefusal = (typeof INVITE_CODE_REFUSALS)[number];
/** What `POST /v1/auth/invites/code` answers when it mailed a code. */
export const InviteCodeSent = z.object({ sent: z.literal(true) });

export const InviteSummary = z.object({
  email: z.string(), locationName: z.string(), accountName: z.string(), existingUser: z.boolean(),
  /** `code` when Accept needs the code mailed to the invite's address (a link invite); absent from a server before A191. */
  verify: z.enum(['code']).nullable().optional(),
});

/**
 * SCR-030 Profile (legacy ProfileView, `PUT auth/update/{id}`; plan 2026-10-15-profile-and-user-menu): the signed-in
 * user's own details, read and saved over the session transport with their access token, as passkey registration is.
 * Legacy's Language offers English and Spanish (`Types/Language.php`); the choice is kept, and the back office stays
 * English until a Spanish translation is built (A84). The colour is one of legacy's `Types/Color.php` values.
 */
export const PROFILE_LANGUAGES = ['en-US', 'es'] as const;
export const PROFILE_COLORS = [
  'blue', 'brown', 'cyan', 'dark-blue', 'dark-green', 'deep-purple', 'gold', 'gray', 'gray-alternate', 'green', 'indigo', 'light-blue',
  'light-green', 'lime-green', 'maroon', 'orange', 'pink', 'purple', 'red', 'salmon', 'teal', 'very-dark',
] as const;

export const ProfileView = z.object({
  userId: z.uuid(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  timeZone: z.string().nullable(),
  language: z.enum(PROFILE_LANGUAGES),
  profileColor: z.enum(PROFILE_COLORS),
});

/** What Save sends. The email is not here: legacy's Save never changed it (B402), and the password has its own dialog. */
export const ProfileUpdate = z.object({
  name: z.string().trim().min(1).max(255),
  phone: z.string().trim().regex(/^[0-9]{10,15}$/).nullable(),
  timeZone: z.string().min(1).max(64).nullable(),
  language: z.enum(PROFILE_LANGUAGES),
  profileColor: z.enum(PROFILE_COLORS),
});

/**
 * Profile's Reset Password dialog: the current password (checked like a sign-in, on its throttle) and the new one (reset's
 * rules: eight characters or more, L512). Legacy asked for no current password (coordinator, 2026-10-15).
 */
export const PasswordChange = z.object({ currentPassword: z.string().min(1).max(1024), password: z.string().min(1).max(1024) });

export type SessionView = z.infer<typeof SessionView>;
export type ProfileView = z.infer<typeof ProfileView>;
export type ProfileUpdate = z.infer<typeof ProfileUpdate>;
export type SignedIn = z.infer<typeof SignedIn>;
export type AccountChoice = z.infer<typeof AccountChoice>;
export type LocationChoice = z.infer<typeof LocationChoice>;
export type InviteSummary = z.infer<typeof InviteSummary>;
export type RefreshScope = z.infer<typeof RefreshScope>;
